import {assert} from 'Asserts';
import {ProcedureRunPlanConditionREST} from 'procedures/network/procedureRunPlanDAO';
import {deriveConfirmationMethodByParamVersionId} from 'procedures/planning/buildFullExperimentPlan';
import {ExperimentalInputs} from 'procedures/planning/ProcedurePlanTypes';
import {ExperimentFlowchart} from 'studies/typed/shared/procedure-runs/design-state/ExperimentalInputs';
import {PlanConditionsByMethodId} from 'studies/typed/shared/procedure-runs/design-state/planConditionState';
import {
  Condition,
  ConditionGroup,
} from 'studies/typed/shared/procedure-runs/design-state/useExperimentDesignState';

import {buildPlanMethodPlanRest} from './planConditionsSerializationUtils';

/**
 * Expands condition groups onto per-method rows for condition id preservation.
 *
 * Every method gets a row at each group's index, so positions line up with the uniform payload.
 * Unlike the save-path conversion, this tolerates partially persisted groups: a null or missing id
 * becomes an id-less row, which preserves nothing.
 */
// TODO(BNCH-172699): Remove once plan state holds PlanConditionsByMethodId directly.
export function planConditionsForPreservation(
  groups: ReadonlyArray<ConditionGroup>,
  methodIds: ReadonlyArray<string>
): PlanConditionsByMethodId {
  const planConditionsByMethodId: PlanConditionsByMethodId = Object.fromEntries(
    methodIds.map((methodId) => [methodId, [] as Array<Condition>])
  );
  groups.forEach((conditionGroup) => {
    const {name, description, numReplicates, conditionIdsByPmdvId} = conditionGroup;
    methodIds.forEach((methodId) => {
      const conditionId = conditionIdsByPmdvId[methodId];
      planConditionsByMethodId[methodId].push({
        name,
        numReplicates,
        ...(description !== undefined ? {description} : {}),
        ...(conditionId != null ? {id: conditionId} : {}),
      });
    });
  });
  return planConditionsByMethodId;
}

/**
 * Serializes preserved experimental inputs for a procedure or version change.
 *
 * Walks each method's rows. `position` is the row's array index, including holes in a sparse array.
 * Callers assign ids and clear unit-operation links after this returns. Values come from the preserved
 * inputs, not evaluated cells.
 */
export function serializePreservedInputsForNetwork(
  preservedInputs: ExperimentalInputs,
  planConditionsByMethodId: PlanConditionsByMethodId,
  methodIdsInOrder: ReadonlyArray<string>
): Array<ProcedureRunPlanConditionREST> {
  const confirmationMethodByParamVersionId = deriveConfirmationMethodByParamVersionId(preservedInputs);
  const payloads: Array<ProcedureRunPlanConditionREST> = [];
  methodIdsInOrder.forEach((methodId) => {
    const rows = planConditionsByMethodId[methodId] ?? [];
    rows.forEach((conditionRow, position) => {
      if (conditionRow == null) {
        return;
      }
      const {name, description, numReplicates} = conditionRow;
      const inputsForMethod = preservedInputs[methodId];
      assert(inputsForMethod != null, `Expected preserved experimental inputs for method ${methodId}`);
      payloads.push({
        name,
        description,
        position,
        templateMethodVersionId: methodId,
        replicateInfos: Array.from({length: numReplicates}, (_, replicateIndex) => ({
          replicateNumber: replicateIndex + 1,
        })),
        methodPlan: buildPlanMethodPlanRest(
          methodId,
          inputsForMethod,
          inputsForMethod.values[position],
          confirmationMethodByParamVersionId
        ),
      });
    });
  });
  return payloads;
}

/**
 * Maps each surviving method's local row to the persisted condition id that should be kept.
 *
 * The result is indexed by local position. A method with no row at that position is omitted.
 * Every row has an id, or none do. The id-less case is a plan with no stored conditions.
 * Primary match is a shared PMDV id. Fallback match is a stable flowchart node id.
 */
export function buildPreservedConditionIdMappings(
  planConditionsByMethodId: PlanConditionsByMethodId,
  oldFlowchart: ExperimentFlowchart,
  newFlowchart: ExperimentFlowchart
): Array<Record<string, string>> {
  assertConditionIdsAreAllPresentOrAllAbsent(planConditionsByMethodId, oldFlowchart);
  const newMethodIdByOldMethodId = methodIdMapBetweenFlowcharts(oldFlowchart, newFlowchart);
  const maxPosition = oldFlowchart.nodes.reduce(
    (max, method) => Math.max(max, planConditionsByMethodId[method.id]?.length ?? 0),
    0
  );
  const mappings: Array<Record<string, string>> = Array.from({length: maxPosition}, () => ({}));
  oldFlowchart.nodes.forEach(({id: oldMethodId}) => {
    const newMethodId = newMethodIdByOldMethodId.get(oldMethodId);
    if (newMethodId == null) {
      return;
    }
    const conditionRows = planConditionsByMethodId[oldMethodId] ?? [];
    conditionRows.forEach((conditionRow, position) => {
      // No stored conditions: every row is id-less, so nothing is preserved.
      if (conditionRow.id == null) {
        return;
      }
      mappings[position][newMethodId] = conditionRow.id;
    });
  });
  return mappings;
}

/** Rejects a plan that has an id on some condition rows and not others. */
function assertConditionIdsAreAllPresentOrAllAbsent(
  planConditionsByMethodId: PlanConditionsByMethodId,
  flowchart: ExperimentFlowchart
): void {
  const rows = flowchart.nodes.flatMap((method) => planConditionsByMethodId[method.id] ?? []);
  const definedRows = rows.filter((row) => row != null);
  const rowsWithIds = definedRows.filter((row) => row.id != null);
  assert(
    rowsWithIds.length === 0 || rowsWithIds.length === definedRows.length,
    'Expected every condition row to have an id, or none of them'
  );
}

function methodIdMapBetweenFlowcharts(
  oldFlowchart: ExperimentFlowchart,
  newFlowchart: ExperimentFlowchart
): Map<string, string> {
  const newMethodIds = new Set(newFlowchart.nodes.map((method) => method.id));
  const newMethodIdByNodeId = new Map(newFlowchart.nodes.map((method) => [method.nodeId, method.id]));
  const newMethodIdByOldMethodId = new Map<string, string>();
  oldFlowchart.nodes.forEach(({id: oldMethodId, nodeId}) => {
    if (newMethodIds.has(oldMethodId)) {
      newMethodIdByOldMethodId.set(oldMethodId, oldMethodId);
      return;
    }
    assert(nodeId != null, 'Expected every method to have a node ID');
    const newMethodId = newMethodIdByNodeId.get(nodeId);
    if (newMethodId != null) {
      newMethodIdByOldMethodId.set(oldMethodId, newMethodId);
    }
  });
  return newMethodIdByOldMethodId;
}
